import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth/auth-context'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PageVisitRow {
  path: string
  visit_count: number
  last_visited_at: string
}

export interface ScoredVisit {
  path: string
  score: number
}

interface VisitEntry {
  path: string
  ts: number
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'zero_page_visits'
const BUFFER_CAP = 500
const SYNC_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000

// ---------------------------------------------------------------------------
// Pure / localStorage helpers
// ---------------------------------------------------------------------------

/** Read the localStorage visit buffer. Returns [] on any error or invalid data. */
export function getLocalBuffer(): VisitEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (e): e is VisitEntry =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as VisitEntry).path === 'string' &&
        typeof (e as VisitEntry).ts === 'number',
    )
  } catch {
    return []
  }
}

/** Append a visit to the localStorage buffer, capping at 500 entries. */
export function recordVisit(path: string): void {
  const buffer = getLocalBuffer()
  buffer.push({ path, ts: Date.now() })
  const trimmed = buffer.slice(-BUFFER_CAP)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed))
}

/**
 * Score a set of page-visit rows by combining frequency and recency.
 *
 * recencyFactor = max(0, 10 * (1 - ageMs / 90daysMs))
 * score = visit_count * 0.6 + recencyFactor * 0.4
 *
 * Returns a new array sorted by score descending.
 */
export function scoreVisits(visits: PageVisitRow[]): ScoredVisit[] {
  const now = Date.now()

  return visits
    .map((v) => {
      const ageMs = now - new Date(v.last_visited_at).getTime()
      const recencyFactor = Math.max(0, 10 * (1 - ageMs / NINETY_DAYS_MS))
      const score = v.visit_count * 0.6 + recencyFactor * 0.4
      return { path: v.path, score }
    })
    .sort((a, b) => b.score - a.score)
}

// ---------------------------------------------------------------------------
// Supabase sync
// ---------------------------------------------------------------------------

/** Guard against concurrent sync calls (C1 fix: race condition). */
let syncing = false

async function syncToSupabase(userId: string): Promise<void> {
  if (syncing) return
  syncing = true

  try {
    const buffer = getLocalBuffer()
    if (buffer.length === 0) return

    // Aggregate local buffer by path
    const aggregated = new Map<string, { count: number; latest: number }>()
    for (const entry of buffer) {
      const existing = aggregated.get(entry.path)
      if (existing) {
        existing.count += 1
        existing.latest = Math.max(existing.latest, entry.ts)
      } else {
        aggregated.set(entry.path, { count: 1, latest: entry.ts })
      }
    }

    // Fetch existing rows for this user
    const paths = Array.from(aggregated.keys())
    const { data: existingRows, error: selectError } = await supabase
      .from('page_visits')
      .select('path, visit_count, last_visited_at')
      .eq('user_id', userId)
      .in('path', paths)

    if (selectError) {
      console.warn('[usePageTracker] Failed to fetch existing visits:', selectError.message)
      return
    }

    // Merge counts and timestamps
    const existingMap = new Map<string, { count: number; lastVisited: number }>()
    if (existingRows) {
      for (const row of existingRows) {
        existingMap.set(row.path as string, {
          count: row.visit_count as number,
          lastVisited: new Date(row.last_visited_at as string).getTime(),
        })
      }
    }

    const upsertRows = Array.from(aggregated.entries()).map(([path, agg]) => {
      const existing = existingMap.get(path)
      return {
        user_id: userId,
        path,
        visit_count: (existing?.count ?? 0) + agg.count,
        last_visited_at: new Date(Math.max(agg.latest, existing?.lastVisited ?? 0)).toISOString(),
      }
    })

    const { error } = await supabase
      .from('page_visits')
      .upsert(upsertRows, { onConflict: 'user_id,path' })

    if (error) {
      console.warn('[usePageTracker] Failed to sync visits:', error.message)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } finally {
    syncing = false
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Track page navigations and periodically sync to Supabase. */
export function usePageTracker(): void {
  const { pathname } = useLocation()
  const { user } = useAuth()

  // Record visit on pathname change (skip auth and module paths)
  useEffect(() => {
    if (pathname.startsWith('/auth') || pathname.startsWith('/module/')) {
      return
    }
    recordVisit(pathname)
  }, [pathname])

  // Sync buffer to Supabase on mount, every 5 min, and on visibility change
  useEffect(() => {
    if (!user) return

    const userId = user.id

    // Initial sync
    syncToSupabase(userId)

    // Periodic sync
    const intervalId = setInterval(() => {
      syncToSupabase(userId)
    }, SYNC_INTERVAL_MS)

    // Visibility change handler — sync when page becomes hidden
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        syncToSupabase(userId)
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [user])
}
